import React from 'react';

export type SortDirection = 'asc' | 'desc';

interface SortableHeaderProps<F extends string> {
  field: F;
  label: string;
  /** The field the table is sorted on right now. */
  activeField: F;
  direction: SortDirection;
  onSort: (field: F) => void;
  numeric?: boolean;
}

/**
 * A table header cell that sorts its column: the label, and a ▲ or ▼ when
 * it is the active sort. The `aria-sort` attribute tells screen readers.
 */
export function SortableHeader<F extends string>({ field, label, activeField, direction, onSort, numeric }: SortableHeaderProps<F>) {
  const active = field === activeField;
  return (
    <th className={numeric ? 'numeric' : undefined} aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="tpl-sort" onClick={() => onSort(field)}>
        {label}
        <span className="tpl-sort-mark" aria-hidden="true">{active ? (direction === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );
}

/** The next sort state after a click: same column flips, a new column starts ascending (or descending for counts and dates). */
export const nextSort = <F extends string>(current: { field: F; direction: SortDirection }, clicked: F, descendingFirst: F[] = []): { field: F; direction: SortDirection } => {
  if (clicked === current.field) return { field: clicked, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  return { field: clicked, direction: descendingFirst.includes(clicked) ? 'desc' : 'asc' };
};
